import React, { PureComponent } from 'react';
import cx from 'classnames';

import './TextFields.scss';

interface Validation {
    required?: boolean;
    pattern?: RegExp;
    patternMessage?: string;
    /** A function that takes the value and returns an error message (or null) */
    fn?: (value: string) => string | null;
}

interface TextFieldProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onInput' | 'defaultValue'> {
    defaultValue?: string | number;
    onChange: (value: string, event: React.FocusEvent<HTMLInputElement>) => void;
    onInput?: (value: string, event: React.FormEvent<HTMLInputElement>) => void;
    validation?: Validation;
    inputClassName?: string;
    inputStyle?: React.CSSProperties;
}

interface TextFieldState {
    error: string | null;
}

export default class TextField extends PureComponent<TextFieldProps, TextFieldState> {
    state: TextFieldState = {
        error: null,
    };

    handleChange = (event: React.FocusEvent<HTMLInputElement>) => {
        const { value } = event.currentTarget;
        const { defaultValue, onChange, validation } = this.props;

        if (value === defaultValue || (value === '' && defaultValue == null)) return;

        let error: string | null = null;
        if (validation) {
            const { required, pattern, patternMessage, fn } = validation;
            error = required && value.trim() === '' ? 'This field is required' :
                pattern && !pattern.test(value) ?
                    patternMessage || `Does not match the pattern: ${pattern.source}` :
                fn && fn(value) ? fn(value) :
                null;
        }

        this.setState({ error });

        if (error) return;

        onChange(value, event);
    };

    handleInput = (event: React.FormEvent<HTMLInputElement>) => {
        const { onInput } = this.props;

        if (!onInput) return;

        onInput(event.currentTarget.value, event);
    };

    render() {
        const { defaultValue, onChange, onInput, validation, inputClassName, inputStyle, style, className, ...rest } = this.props;
        const { error } = this.state;

        return (
            <span style={style} className={cx('TextField-root', className)}>
                <input
                    type='text'
                    {...rest}
                    key={defaultValue}
                    defaultValue={defaultValue}
                    onBlur={this.handleChange}
                    onInput={this.handleInput}
                    className={cx('TextField-field', inputClassName)}
                    style={inputStyle}
                />

                {!error ? null :
                    <div className={'TextField-error'}>
                        <div>{error}</div>
                    </div>
                }
            </span>
        );
    }
}
